// src/templates/industries/GenericResume.jsx
import React from 'react';

const GenericResume = ({ industry, sections, data, template }) => {
  const { personalInfo, skills, experience, education, certifications, languages } = data || {};

  // Get industry-specific data
  const industryData = data?.industryData || {};

  // Helper to render section content based on type
  const renderSectionContent = (section, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;

    switch (section.type) {
      case 'skills':
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {value.map((item, idx) => (
              <span key={idx} style={{ 
                background: '#ecf0f1', 
                padding: '2px 10px', 
                borderRadius: 12, 
                fontSize: 13 
              }}>
                {typeof item === 'string' ? item : item.name || item}
              </span>
            ))}
          </div>
        );

      case 'flyingHours':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {Object.entries(value).map(([key, val]) => {
              const labels = {
                totalHours: 'Total Hours',
                multiEngine: 'Multi-Engine',
                instrument: 'Instrument',
                night: 'Night',
                crossCountry: 'Cross-Country',
                turbine: 'Turbine'
              };
              return (
                <div key={key}>
                  <span style={{ fontWeight: 500 }}>{labels[key] || key}:</span>
                  <span style={{ marginLeft: 4 }}>{val || 0}</span>
                </div>
              );
            })}
          </div>
        );

      case 'text':
      case 'textarea':
        return <p style={{ margin: 0 }}>{value}</p>;

      case 'experience':
      case 'flight-experience':
        return Array.isArray(value) && value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8, paddingLeft: 8, borderLeft: '2px solid #3498db' }}>
            {item.position && <div><strong>{item.position}</strong> {item.company && `at ${item.company}`}</div>}
            {item.airline && <div><strong>{item.airline}</strong> {item.position && `- ${item.position}`}</div>}
            {item.dates && <div style={{ fontSize: 13, color: '#7f8c8d' }}>{item.dates}</div>}
            {item.responsibilities && Array.isArray(item.responsibilities) && (
              <ul style={{ margin: '4px 0 0 16px', fontSize: 13 }}>
                {item.responsibilities.map((resp, i) => (
                  <li key={i}>{resp}</li>
                ))}
              </ul>
            )}
            {item.description && <p style={{ fontSize: 13, margin: '4px 0 0' }}>{item.description}</p>}
          </div>
        ));

      case 'projects':
        return Array.isArray(value) && value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <strong>{item.projectName || item.name}</strong>
            {item.description && <span style={{ color: '#7f8c8d' }}> - {item.description}</span>}
            {item.technologies && <span style={{ fontSize: 13, color: '#555' }}> | {item.technologies}</span>}
          </div>
        ));

      case 'publications':
        return Array.isArray(value) && value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <strong>{item.title}</strong>
            {item.journal && <span style={{ color: '#7f8c8d' }}> - {item.journal}</span>}
            {item.year && <span style={{ color: '#7f8c8d' }}> ({item.year})</span>}
            {item.doi && <span style={{ fontSize: 12, color: '#3498db' }}> DOI: {item.doi}</span>}
          </div>
        ));

      case 'education':
        return Array.isArray(value) && value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <strong>{item.degree}</strong>
            {item.institution && <span style={{ color: '#3498db' }}> - {item.institution}</span>}
            {item.year && <span style={{ color: '#7f8c8d', fontSize: 13 }}> ({item.year})</span>}
            {item.honors && <span style={{ fontSize: 13, color: '#555' }}> - {item.honors}</span>}
          </div>
        ));

      case 'certifications':
        return Array.isArray(value) && value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <strong>{item.name}</strong>
            {item.issuer && <span style={{ color: '#7f8c8d' }}> - {item.issuer}</span>}
            {item.year && <span style={{ color: '#7f8c8d', fontSize: 13 }}> ({item.year})</span>}
          </div>
        ));

      default:
        return <p>{JSON.stringify(value)}</p>;
    }
  };

  return (
    <div style={{ 
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      color: '#333',
      lineHeight: 1.6,
      padding: '20px',
      background: 'white'
    }}>
      {/* Header / Personal Info */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 20, 
        paddingBottom: 20, 
        borderBottom: '2px solid #2c3e50' 
      }}>
        {personalInfo?.photo_url && (
          <img 
            src={personalInfo.photo_url} 
            alt="Profile" 
            style={{ 
              width: 120, 
              height: 120, 
              borderRadius: '50%', 
              objectFit: 'cover', 
              marginBottom: 10,
              border: '3px solid #2c3e50'
            }}
          />
        )}
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 4, color: '#2c3e50' }}>
          {personalInfo?.full_name || data?.full_name || 'Your Name'}
        </h1>
        <p style={{ fontSize: 18, color: '#7f8c8d', marginBottom: 8 }}>
          {personalInfo?.title || data?.title || 'Professional Title'}
        </p>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 20, 
          flexWrap: 'wrap', 
          fontSize: 14, 
          color: '#555' 
        }}>
          {(personalInfo?.email || data?.email) && <span>📧 {personalInfo?.email || data?.email}</span>}
          {(personalInfo?.phone || data?.phone) && <span>📱 {personalInfo?.phone || data?.phone}</span>}
          {(personalInfo?.address || data?.address) && <span>📍 {personalInfo?.address || data?.address}</span>}
        </div>
      </div>

      {/* Summary */}
      {(personalInfo?.summary || data?.summary) && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Professional Summary
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: '#555' }}>
            {personalInfo?.summary || data?.summary}
          </p>
        </div>
      )}

      {/* Experience */}
      {(experience || data?.experience) && (experience || data?.experience).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Experience
          </h3>
          {(experience || data?.experience || []).map((exp, idx) => (
            <div key={idx} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#2c3e50' }}>{exp.title}</span>
                <span style={{ color: '#3498db' }}>{exp.company}</span>
                <span style={{ color: '#7f8c8d', fontSize: 14 }}>{exp.startDate} - {exp.endDate}</span>
              </div>
              {exp.description && (
                <p style={{ marginTop: 4, fontSize: 14, color: '#555' }}>{exp.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Education */}
      {(education || data?.education) && (education || data?.education).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Education
          </h3>
          {(education || data?.education || []).map((edu, idx) => (
            <div key={idx} style={{ marginBottom: 4 }}>
              <strong>{edu.degree}</strong>
              <span style={{ color: '#3498db', marginLeft: 8 }}>{edu.institution}</span>
              <span style={{ color: '#7f8c8d', fontSize: 14, marginLeft: 8 }}>{edu.year}</span>
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {(skills || data?.skills) && (skills || data?.skills).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Skills
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(skills || data?.skills || []).map((skill, idx) => (
              <span key={idx} style={{ 
                background: '#ecf0f1', 
                padding: '4px 12px', 
                borderRadius: 20, 
                fontSize: 14, 
                color: '#2c3e50' 
              }}>
                {typeof skill === 'string' ? skill : skill.name || skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Industry-Specific Sections */}
      {sections && sections.length > 0 && (
        <div>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            {industry?.name || 'Industry'} Details
          </h3>
          {sections.map((section) => {
            const value = industryData[section.id];
            if (!value || (Array.isArray(value) && value.length === 0)) return null;
            
            return (
              <div key={section.id} style={{ marginBottom: 12 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#34495e', marginBottom: 4 }}>
                  {section.icon} {section.label}
                </h4>
                <div style={{ fontSize: 14, color: '#555' }}>
                  {renderSectionContent(section, value)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Certifications */}
      {(certifications || data?.certifications) && (certifications || data?.certifications).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Certifications
          </h3>
          {(certifications || data?.certifications || []).map((cert, idx) => (
            <div key={idx} style={{ fontSize: 14, marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>{cert.name}</span>
              {cert.issuer && <span style={{ color: '#7f8c8d' }}> - {cert.issuer}</span>}
              {cert.year && <span style={{ color: '#7f8c8d' }}> ({cert.year})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Languages */}
      {(languages || data?.languages) && (languages || data?.languages).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ 
            fontSize: 16, 
            fontWeight: 600, 
            color: '#2c3e50', 
            borderBottom: '2px solid #3498db', 
            paddingBottom: 4, 
            marginBottom: 10 
          }}>
            Languages
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {(languages || data?.languages || []).map((lang, idx) => (
              <span key={idx} style={{ fontSize: 14 }}>
                {typeof lang === 'object' ? `${lang.name} - ${lang.proficiency || ''}` : lang}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericResume;